/**
 * ConnectorsPage — grid of connector types on top, list of existing
 * connectors below. Clicking a type tile opens the config dialog in
 * create mode; clicking an existing row opens it in edit mode.
 *
 * A `?gi=<root_id>` query string parameter (produced by the Vault's
 * "Connect" button) opens the create flow pre-linked to that GI and
 * auto-picks a sensible default type (REST API).
 */

import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { EmptyState } from '@/components/EmptyState';
import { ErrorState } from '@/components/ErrorState';
import { Spinner } from '@/components/LoadingState';
import type { ConnectorType, EntityId } from '@/api/types';

import { useConnectorList } from '@/hooks/useConnectors';
import { useVaultSnapshot } from '@/hooks/useVault';

import { AxiomStatusChip } from './AxiomStatusChip';

import { ConnectorConfigDialog, type DialogMode } from './ConnectorConfigDialog';
import { ConnectorRow } from './ConnectorRow';
import { ConnectorTypeCard } from './ConnectorTypeCard';

const TYPES: ConnectorType[] = [
  'rest_api',
  'webhook',
  'email',
  'file_storage',
  'database',
  'scheduler',
];

export function ConnectorsPage() {
  const { t } = useTranslation(['connectors', 'common']);
  const [searchParams, setSearchParams] = useSearchParams();

  const connectorsQuery = useConnectorList();
  const vaultQuery = useVaultSnapshot();
  const gis = useMemo(() => vaultQuery.data?.gis ?? [], [vaultQuery.data]);

  const [dialogMode, setDialogMode] = useState<DialogMode | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  // Handle deep link from the Vault's "Connect" button: ?gi=<id>.
  // Opens the create dialog with REST API pre-picked and the GI
  // pre-linked. Clear the query once consumed so a dialog close
  // doesn't re-trigger on refocus.
  useEffect(() => {
    const giId = searchParams.get('gi') as EntityId | null;
    if (giId) {
      setDialogMode({ kind: 'create', type: 'rest_api', linkedGi: giId });
      setDialogOpen(true);
      const next = new URLSearchParams(searchParams);
      next.delete('gi');
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function openCreate(type: ConnectorType) {
    setDialogMode({ kind: 'create', type });
    setDialogOpen(true);
  }

  function openEdit(id: string) {
    const connector = connectorsQuery.data?.find((c) => c.id === id);
    if (!connector) return;
    setDialogMode({ kind: 'edit', connector });
    setDialogOpen(true);
  }

  return (
    <div className="flex flex-col gap-8">
      <header className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-ink">{t('title')}</h1>
          <p className="mt-1 text-sm text-ink-muted">{t('subtitle')}</p>
        </div>
        <AxiomStatusChip />
      </header>

      {/* Type picker */}
      <section aria-labelledby="connectors-add-heading" className="flex flex-col gap-4">
        <h2
          id="connectors-add-heading"
          className="text-sm font-semibold uppercase tracking-wider text-ink-muted"
        >
          {t('add.heading')}
        </h2>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {TYPES.map((type) => (
            <ConnectorTypeCard key={type} type={type} onPick={openCreate} />
          ))}
        </div>
      </section>

      {/* Existing list */}
      <section aria-labelledby="connectors-list-heading" className="flex flex-col gap-4">
        <h2
          id="connectors-list-heading"
          className="text-sm font-semibold uppercase tracking-wider text-ink-muted"
        >
          {t('list.heading')}
        </h2>

        {connectorsQuery.isPending ? (
          <div className="flex items-center justify-center py-12">
            <Spinner size="md" />
          </div>
        ) : connectorsQuery.isError ? (
          <ErrorState
            title={t('list.error_title')}
            body={t('list.error_body')}
            onRetry={() => void connectorsQuery.refetch()}
          />
        ) : connectorsQuery.data.length === 0 ? (
          <EmptyState title={t('list.empty_title')} body={t('list.empty_body')} />
        ) : (
          <ul className="flex flex-col gap-2">
            {connectorsQuery.data.map((c) => (
              <li key={c.id}>
                <ConnectorRow connector={c} onEdit={openEdit} />
              </li>
            ))}
          </ul>
        )}
      </section>

      <ConnectorConfigDialog
        mode={dialogMode}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        gis={gis}
      />
    </div>
  );
}
